import { getFormat } from "@/helpers/gameHelpers";
import { cn, pluralize } from "@/lib/utils";
import { useGameStore } from "@/store/gameStore";
import { useConvexConnectionState } from "convex/react";
import { ChevronUp } from "lucide-react";
import React, { useEffect, useRef } from "react";

/**
 * The court to head to, big enough to read at arm's length, and how many
 * cards are left. Tapping it opens the game menu.
 */
export const CourtFace: React.FC<React.ComponentProps<"button">> = ({
  className,
  ...props
}) => {
  const card = useGameStore((state) => state.currentCard);
  const format = useGameStore((state) =>
    getFormat(state.currentCard, state.game.cards),
  );
  const players = useGameStore((state) => state.game.players);
  const lastDrawn = useGameStore((state) => state.game.lastDrawn);
  const deckSize = useGameStore((state) => state.game.cards?.length ?? 0);
  const slug = useGameStore((state) => state.game.slug);
  const drawnIndex = useGameStore((state) => state.drawnIndex);
  const { isWebSocketConnected } = useConvexConnectionState();

  // Whether the player last moved forward through their cards, so the
  // court name slides in from the right side.
  const previousIndex = useRef(drawnIndex);
  const forward = drawnIndex >= previousIndex.current;
  useEffect(() => {
    previousIndex.current = drawnIndex;
  }, [drawnIndex]);

  const left = deckSize - (lastDrawn + 1);
  const title = !card
    ? `${players} Players`
    : card.suit === "joker"
      ? format
      : `Court ${card.court}`;
  const detail =
    slug && !isWebSocketConnected
      ? "Connecting…"
      : lastDrawn < 0
        ? "Draw to start"
        : left === 0
          ? "All cards drawn 🎉"
          : `${left} ${pluralize("card", left)} left`;

  return (
    <button
      type="button"
      className={cn(
        "flex w-full flex-col items-center py-3.5 transition-[scale,opacity] duration-150 select-none active:scale-[0.97] active:opacity-80",
        className,
      )}
      {...props}
    >
      <span
        key={title}
        className={cn(
          "animate-in fade-in block text-[min(60px,15vw)] leading-[1.2] font-extrabold tracking-tight whitespace-nowrap uppercase duration-300",
          forward ? "slide-in-from-right-8" : "slide-in-from-left-8",
        )}
      >
        {title}
      </span>
      <span className="flex items-center gap-1.5 text-xl font-semibold opacity-70">
        <span key={detail} className="animate-in fade-in duration-300">
          {detail}
        </span>
        {/* The menu grows upward from the card. */}
        <ChevronUp className="size-5" strokeWidth={3.5} aria-hidden />
      </span>
      <span className="sr-only">Opens the game menu</span>
    </button>
  );
};
